import React, { useEffect, useRef, useState, useCallback } from 'react';
import './about.css';
import { Link, useLocation } from 'react-router-dom';
import MobileNav from '../common/MobileNav';

const About = () => {
  const heroRef = useRef(null);
  const location = useLocation();


  // STATE MANAGEMENT - All Features
  // ==============================================
  
  // Existing States
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showBackToTop, setShowBackToTop] = useState(false);

  // 1. Dark/Light Mode Toggle
  const [theme, setTheme] = useState('dark');

  // 2. Sticky Navigation
  const [isSticky, setIsSticky] = useState(false);

  // 4. Toast Notifications
  const [toasts, setToasts] = useState([]);

  // 5. Image Lightbox
  const [lightbox, setLightbox] = useState({ isOpen: false, imageUrl: '', index: 0, images: [] });


  // 10. Loading Animation
  const [loading, setLoading] = useState(true);


  // FEATURE 1: DARK/LIGHT MODE TOGGLE
  // ==============================================
  
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'dark';
    setTheme(savedTheme);
    document.documentElement.setAttribute('data-theme', savedTheme);
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
    showToast(`Switched to ${newTheme} mode`, 'success');
  };

  // ==============================================
  // FEATURE 2: STICKY NAVIGATION
  // ==============================================
  
  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ==============================================
  // FEATURE 4: TOAST NOTIFICATIONS
  // ==============================================
  
  const showToast = useCallback((message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 5000);
  }, []);

  // ==============================================
  // FEATURE 5: IMAGE LIGHTBOX
  // ==============================================
  
  const openLightbox = useCallback((imageUrl, index, allImages) => {
    setLightbox({ isOpen: true, imageUrl, index, images: allImages });
    document.body.style.overflow = 'hidden';
  }, []);

  const closeLightbox = useCallback(() => {
    setLightbox({ isOpen: false, imageUrl: '', index: 0, images: [] });
    document.body.style.overflow = 'auto';
  }, []);

  const navigateLightbox = useCallback((direction) => {
    setLightbox(prev => {
      const newIndex = direction === 'next' 
        ? (prev.index + 1) % prev.images.length
        : (prev.index - 1 + prev.images.length) % prev.images.length;
      return { ...prev, index: newIndex, imageUrl: prev.images[newIndex] };
    });
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightbox.isOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') navigateLightbox('next');
      if (e.key === 'ArrowLeft') navigateLightbox('prev');
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightbox.isOpen, closeLightbox, navigateLightbox]);

  // ==============================================
  // FEATURE 10: LOADING ANIMATION
  // ==============================================
  
  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1500);
    return () => clearTimeout(timer);
  }, []);

  // ==============================================
  // EXISTING FUNCTIONALITY
  // ==============================================

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          // Parallax effect
          if (heroRef.current) {
            const scrolled = window.pageYOffset;
            heroRef.current.style.transform = `translateY(${scrolled * 0.4}px)`;
          }

          // Scroll progress
          const windowHeight = window.innerHeight;
          const documentHeight = document.documentElement.scrollHeight;
          const scrollTop = window.pageYOffset;
          const progress = (scrollTop / (documentHeight - windowHeight)) * 100;
          setScrollProgress(progress);

          // Back to top button
          setShowBackToTop(scrollTop > 300);

          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ==============================================
  // FEATURE 6: STAGGER ANIMATIONS
  // ==============================================
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Get all visible stagger items
            const visibleItems = Array.from(
              document.querySelectorAll('.about-stat-card, .about-value-card')
            ).filter(item => {
              const rect = item.getBoundingClientRect();
              return rect.top < window.innerHeight && rect.bottom > 0;
            });
            
            // Add stagger effect to each visible item
            visibleItems.forEach((item, index) => {
              setTimeout(() => {
                item.classList.add('animate-in');
              }, index * 100);
            });
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    const cards = document.querySelectorAll('.about-stat-card, .about-value-card');
    cards.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  // Scroll to top
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const stats = [
    { value: '3+', label: 'Success in getting happy customer' },
    { value: '5+', label: 'Successful business' },
    { value: '6+', label: 'Total clients who love Elexyt' },
    { value: '4★', label: 'Stars reviews given by satisfied clients' }
  ];

  const values = [
    {
      icon: (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2L2 7l10 5 10-5-10-5z" />
          <path d="M2 17l10 5 10-5" />
          <path d="M2 12l10 5 10-5" />
        </svg>
      ),
      title: 'Innovation',
      description: 'Pushing the boundaries of what\'s possible with cutting-edge technology solutions.'
    },
    {
      icon: (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
      ),
      title: 'Excellence',
      description: 'Commitment to delivery excellence is a cornerstone of our success and key differentiator.'
    },
    {
      icon: (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      ),
      title: 'Security',
      description: 'Driving a smarter, more secure, and sustainable future for businesses worldwide.'
    },
    {
      icon: (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      ),
      title: 'Collaboration',
      description: 'Working together with clients to achieve digital transformation goals.'
    }
  ];

  const storyImages = [
    'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800',
    'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800'
  ];
  
  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="loading-spinner">
            <div className="spinner-circle"></div>
            <div className="spinner-logo">E</div>
          </div>
          <h2 className="loading-text">Elexyt Technologies</h2>
        </div>
      </div>
    );
  }

  // ==============================================
  // MAIN RENDER
  // ==============================================

  return (
    <>
      {/* Scroll Progress Bar */}
      <div className="scroll-progress" style={{ width: `${scrollProgress}%` }} />

      {/* Toast Notifications */}
      <div className="toast-container">
        {toasts.map(toast => (
          <div key={toast.id} className={`toast toast-${toast.type}`}>
            <div className="toast-icon">
              {toast.type === 'success' && '✓'}
              {toast.type === 'error' && '✕'}
              {toast.type === 'info' && 'ℹ'}
            </div>
            <span className="toast-message">{toast.message}</span>
            <button 
              className="toast-close"
              onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
              aria-label="Close notification"
            >
              ✕
            </button>
          </div>
        ))}
      </div>

      {/* Theme Toggle Button */}
      <button 
        className="theme-toggle"
        onClick={toggleTheme}
        aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
      >
        {theme === 'dark' ? '☀️' : '🌙'}
      </button>

      {/* Image Lightbox */}
      {lightbox.isOpen && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <button className="lightbox-close" onClick={closeLightbox} aria-label="Close lightbox">
            ✕
          </button>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img src={lightbox.imageUrl} alt="About preview" className="lightbox-image" />
            {lightbox.images.length > 1 && (
              <>
                <button 
                  className="lightbox-nav lightbox-prev"
                  onClick={() => navigateLightbox('prev')}
                  aria-label="Previous image"
                >
                  ‹
                </button>
                <button 
                  className="lightbox-nav lightbox-next"
                  onClick={() => navigateLightbox('next')}
                  aria-label="Next image"
                >
                  ›
                </button>
              </>
            )}
            <div className="lightbox-caption">
              {lightbox.index + 1} / {lightbox.images.length}
            </div>
          </div>
        </div>
      )}

      <div className="about">
        {/* Hero Section */}
        <section className="about-hero">
          <div className="about-hero-background" ref={heroRef}>
            <div className="about-hero-pattern"></div>
            <div className="about-hero-gradient"></div>
          </div>

          <div className="container">
            <div className="about-hero-content">
              <div className="about-badge-wrapper">
                <span className="about-badge">
                  <span className="badge-dot"></span>
                  About Elexyt Technologies
                </span>
              </div>

              <h1 className="about-hero-title">
                About Elexyt Technologies And It's
                <span className="about-title-gradient"> Innovative IT Solutions</span>
              </h1>

              <p className="about-hero-description">
                Elexyt Technologies is revolutionizing the IT landscape with its cutting-edge approach to digital transformation.
              </p>
            </div>
          </div>

          {/* Stats Cards */}
          {/* <div className="about-stats">
            <div className="container">
              <div className="about-stats-wrapper">
                {stats.map((stat, index) => (
                  <div key={index} className="about-stat-card">
                    <div className="about-stat-value">{stat.value}</div>
                    <div className="about-stat-label">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div> */}
        </section>

        {/* Story Section */}
        <section className="about-story-section">
          <div className="container">
            <div className="about-story-grid">
              <div className="about-images-wrapper">
                <div 
                  className="about-image-main"
                  onClick={() => openLightbox(storyImages[0], 0, storyImages)}
                  style={{ cursor: 'pointer' }}
                >
                  <img
                    src={storyImages[0]}
                    alt="Team collaboration at Elexyt Technologies"
                  />
                  <div className="about-image-overlay"></div>
                </div>
                <div 
                  className="about-image-floating"
                  onClick={() => openLightbox(storyImages[1], 1, storyImages)}
                  style={{ cursor: 'pointer' }}
                >
                  <img
                    src={storyImages[1]}
                    alt="Technology innovation workspace"
                  />
                </div>
              </div>

              <div className="about-story-content">
                <div className="section-label">
                  <span className="label-line"></span>
                  <span>About Us</span>
                </div>
                <h2 className="section-title-large">
                  About Elexyt Technologies And It's Innovative IT Solutions
                </h2>
                <p className="about-text">
                  Elexyt Technologies is revolutionizing the IT landscape with its cutting-edge approach to digital transformation. Elexyt Technologies's commitment to delivery excellence is a cornerstone of its success and a key differentiator in the competitive IT landscape.
                </p>
                <p className="about-text">
                  With a dedication to pushing the boundaries of what's possible, Elexyt Technologies is not just keeping pace with industry trends but setting them, driving a smarter, more secure, and sustainable future for businesses worldwide.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="about-values-section">
          <div className="container">
            <div className="section-header centered">
              <div className="section-label">
                <span className="label-line"></span>
                <span>Our Values</span>
                <span className="label-line"></span>
              </div>
              <h2 className="section-title-large">What Drives Us Forward</h2>
              <p className="section-subtitle">
                Our core values shape everything we do, from client relationships to technological innovation.
              </p>
            </div>

            <div className="about-values-grid">
              {values.map((value, index) => (
                <div key={index} className="about-value-card">
                  <div className="about-value-icon">
                    {value.icon}
                  </div>
                  <h3 className="about-value-title">{value.title}</h3>
                  <p className="about-value-description">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="about-mission-section">
          <div className="container">
            <div className="about-mission-grid">
              <div className="about-mission-content">
                <div className="section-label">
                  <span className="label-line"></span>
                  <span>Our Mission</span>
                </div>
                <h2 className="section-title-large">
                  Transforming Businesses Through Technology
                </h2>
                <p className="about-text">
                  Elexyt Technologies is a forward-thinking IT company dedicated to delivering innovative solutions that drive digital transformation.
                </p>
                <p className="about-text">
                  We believe in creating sustainable, scalable solutions that empower businesses to thrive in an increasingly digital world. Our approach combines technical expertise with strategic thinking to deliver measurable results.
                </p>
              </div>

              <div 
                className="about-mission-image"
                onClick={() => openLightbox('https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800', 0, ['https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800'])}
                style={{ cursor: 'pointer' }}
              >
                <img
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800"
                  alt="Digital transformation and technology innovation"
                />
                <div className="about-image-overlay"></div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="about-cta-section">
          <div className="about-cta-pattern"></div>
          <div className="container">
            <div className="about-cta-content">
              <h2 className="about-cta-title">Ready to Transform Your Business?</h2>
              <p className="about-cta-description">
                Partner with Elexyt Technologies and experience the power of innovative IT solutions tailored to your business needs.
              </p>
              <div className="about-cta-actions">
                <Link to="/contact" className="btn btn-light btn-large">
                  Get in Touch
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </Link>
                <Link to="/service" className="btn btn-outline-light-alt btn-large">
                  Our Services
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>

      <button
        className={`back-to-top ${showBackToTop ? 'visible' : ''}`}
        onClick={scrollToTop}
        aria-label="Scroll to top"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="18 15 12 9 6 15" />
        </svg>
      </button>
      <MobileNav />
    </>
  );
};

export default About;